import PropTypes from 'prop-types';

import { Box, ButtonGroup, Icon, Image, Layout, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconPaintRollerFilled } from '@zillow/constellation-icons';

import { HeroButton } from '../../styles';

import { PRODUCT_INTRO_TYPES } from '../../../../../containers/RentGuaranteePage/constants';

import MissedRentIcon from '../../../../../images/rentalProtection/missed-rent.svg';
import PropertyDamageIcon from '../../../../../images/rentalProtection/property-damage.svg';
import PetDamageIcon from '../../../../../images/rentalProtection/pet-damage.svg';
import ExcessCleaningIcon from '../../../../../images/rentalProtection/excess-cleaning.svg';
import EvictionCostsIcon from '../../../../../images/rentalProtection/eviction-costs.svg';

import { gridContentStyles } from './styles';

const propTypes = {
  onInterested: PropTypes.func,
  onNotInterested: PropTypes.func,
  onSeeCoverageExamples: PropTypes.func,
  productIntroType: PropTypes.oneOf(Object.values(PRODUCT_INTRO_TYPES)).isRequired,
  isActiveProductTypePremium: PropTypes.bool,
  isInModal: PropTypes.bool,
  marginBottom: PropTypes.string,
};

const defaultProps = {
  onInterested: () => {},
  onNotInterested: () => {},
  onSeeCoverageExamples: () => {},
  isActiveProductTypePremium: false,
  isInModal: false,
  marginBottom: '1800',
};

const RentalProtectionWhatsCoveredSection = ({
  onInterested,
  onNotInterested,
  onSeeCoverageExamples,
  productIntroType,
  isActiveProductTypePremium,
  isInModal,
  marginBottom,
}) => {
  return (
    <div data-testid="RentalProtectionWhatsCoveredSection">
      {productIntroType === PRODUCT_INTRO_TYPES.POST_ACTIVATION_ILLUSTRATIVE && (
        <Box css={{ md: { display: 'none' } }}>
          <Box css={{ marginBottom: '600' }}>
            <ButtonGroup>
              <HeroButton
                tone="brand"
                emphasis="filled"
                onClick={onInterested('top')}
                data-testid="ProductIntroIllustrative-WhatsCovered-InterestedBtn"
              >
                Find out more
              </HeroButton>
              <TextButton
                onClick={onNotInterested('top')}
                data-testid="ProductIntroIllustrative-WhatsCovered-NotInterestedBtn"
                css={{ paddingRight: '0' }}
              >
                {isActiveProductTypePremium ? 'No thanks, finish up' : 'No thanks, skip'}
              </TextButton>
            </ButtonGroup>
          </Box>
        </Box>
      )}
      <Paragraph
        css={{
          textStyle: 'heading-sm',
          marginBottom: '200',
          fontFamily: 'heading-alt',
          color: 'text.express.insight.strong',
        }}
      >
        What&apos;s covered?
      </Paragraph>
      <Box css={{ mdDown: { display: 'none' } }}>
        <Paragraph css={{ marginBottom: '400' }}>Examples of potential covered events:</Paragraph>
      </Box>
      <Box css={{ marginTop: '600', marginBottom: marginBottom }}>
        <Layout.Root css={{ textAlign: 'center', gap: '600' }}>
          <Layout.Content css={gridContentStyles}>
            <Image src={MissedRentIcon} alt="dollar sign" css={{ width: '44px', height: '44px' }} />
            <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBottom: '200' }}>
              Missed Rent
            </Paragraph>
            <Box css={{ mdDown: { display: 'none' } }}>
              <Paragraph color="text.subtle" css={{ textAlign: 'left' }}>
                If your renter falls on tough times, coverage may help recoup lost rent.
              </Paragraph>
            </Box>
          </Layout.Content>
          <Layout.Content css={gridContentStyles}>
            <Image src={PropertyDamageIcon} alt="broken property" css={{ width: '44px', height: '44px' }} />
            <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBottom: '200' }}>
              Property damage
            </Paragraph>
            <Box css={{ mdDown: { display: 'none' } }}>
              <Paragraph color="text.subtle" css={{ textAlign: 'left' }}>
                Claim the cost for damage to carpet, walls, floors, fixtures, and appliances.
              </Paragraph>
            </Box>
          </Layout.Content>
          <Layout.Content css={gridContentStyles}>
            <Image src={PetDamageIcon} alt="pet paw" css={{ width: '44px', height: '44px' }} />
            <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBottom: '200' }}>
              Pet damage
            </Paragraph>
            <Box css={{ mdDown: { display: 'none' } }}>
              <Paragraph color="text.subtle" css={{ textAlign: 'left' }}>
                Confidently rent to pet owners with coverage for things like carpets or fixtures.
              </Paragraph>
            </Box>
          </Layout.Content>
          <Layout.Content css={gridContentStyles}>
            <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconPaintRollerFilled />} />
            <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBottom: '200' }}>
              Repaint worn surfaces
            </Paragraph>
            <Box css={{ mdDown: { display: 'none' } }}>
              <Paragraph color="text.subtle" css={{ textAlign: 'left' }}>
                Claim time and materials for repainting significant wear, beyond touch ups.
              </Paragraph>
            </Box>
          </Layout.Content>
          <Layout.Content css={gridContentStyles}>
            <Image src={ExcessCleaningIcon} alt="trash can" css={{ width: '44px', height: '44px' }} />
            <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBottom: '200' }}>
              Excess cleaning
            </Paragraph>
            <Box css={{ mdDown: { display: 'none' } }}>
              <Paragraph color="text.subtle" css={{ textAlign: 'left' }}>
                If your renter leaves behind an excessive mess, you may be reimbursed for clean up.
              </Paragraph>
            </Box>
          </Layout.Content>
          <Layout.Content css={gridContentStyles}>
            <Image src={EvictionCostsIcon} alt="scales of justice" css={{ width: '44px', height: '44px' }} />
            <Paragraph css={{ textStyle: { base: 'body-lg-bold', smDown: 'body' }, marginBottom: '200' }}>
              Eviction costs
            </Paragraph>
            <Box css={{ mdDown: { display: 'none' } }}>
              <Paragraph color="text.subtle" css={{ textAlign: 'left' }}>
                In the event of an eviction, proceeding costs and legal fees may be claimed.
              </Paragraph>
            </Box>
          </Layout.Content>
        </Layout.Root>
        {!isInModal && (
          <Box css={{ md: { display: 'none' } }}>
            <Paragraph css={{ marginTop: '600', textAlign: 'center' }}>
              <TextButton onClick={onSeeCoverageExamples}>See coverage examples</TextButton>
            </Paragraph>
          </Box>
        )}
      </Box>
    </div>
  );
};

RentalProtectionWhatsCoveredSection.propTypes = propTypes;
RentalProtectionWhatsCoveredSection.defaultProps = defaultProps;

export default RentalProtectionWhatsCoveredSection;
